/**
 * AdMob configuration. Ads are a native-only concern: the web showcase and any
 * environment without the native module use the `.web` stubs and never load an
 * ad. Test ad unit ids are used in development so we NEVER serve or click a live
 * ad while building (clicking your own live ads gets the AdMob account banned).
 *
 * Real unit ids come from EXPO_PUBLIC_ADMOB_* (EAS env / .env) and are inlined at
 * build/update time — static member access is REQUIRED for Expo to inline them.
 */
import { Platform } from 'react-native';

const APP_OPEN = process.env.EXPO_PUBLIC_ADMOB_APP_OPEN_UNIT;
const REWARDED = process.env.EXPO_PUBLIC_ADMOB_REWARDED_UNIT;
const INTERSTITIAL = process.env.EXPO_PUBLIC_ADMOB_INTERSTITIAL_UNIT;
const BANNER = process.env.EXPO_PUBLIC_ADMOB_BANNER_UNIT;

const isReal = (unit: string | undefined): unit is string =>
  Boolean(unit && unit.startsWith('ca-app-pub'));

/**
 * Master kill-switch. OFF on web (no native SDK) AND OFF until a REAL App Open
 * unit is configured — so no test ad ever shows to users before the AdMob
 * account is set up. Configuring the env vars flips every format on.
 */
export const ADS_ENABLED = Platform.OS !== 'web' && isReal(APP_OPEN);

/** Delay after the app opens before the full-screen App Open ad appears (ms). */
export const APP_OPEN_DELAY_MS = 12_000;

/** Min gap between interstitials so they never feel spammy. */
export const INTERSTITIAL_MIN_GAP_MS = 3 * 60_000;

/** Coins granted per rewarded-ad view (must match grant_ad_reward + AdMob unit). */
export const REWARDED_AD_COINS = 25;

/** Dev → Google test unit; prod → the real one when configured, else test. */
const pick = (real: string | undefined, testUnitId: string): string =>
  __DEV__ ? testUnitId : isReal(real) ? real : testUnitId;

export const appOpenAdUnitId = (testUnitId: string): string => pick(APP_OPEN, testUnitId);
export const rewardedAdUnitId = (testUnitId: string): string => pick(REWARDED, testUnitId);
export const interstitialAdUnitId = (testUnitId: string): string => pick(INTERSTITIAL, testUnitId);
export const bannerAdUnitId = (testUnitId: string): string => pick(BANNER, testUnitId);
